/* eslint-disable react/button-has-type */
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { colors as fixedColors } from '_styles';
import { _ } from '_locales';
import moment from 'moment';

import {
  AlertIt,
  WebappHeader,
  WebappFooter,
  PromptMessage,
  WebappSubHeader,
  CustomMessage
} from '_components';
import images from '_images';
import apis, { webCampaignBaseUrl } from '_apis';
import {
  alert as alertSetup
} from '_actions';

import Campaign from '../pages/Campaign';
/**
 * Campaign interactions
 */
import handleAction from '../pages/Campaign/handleAction';
import NSExcluded from './NSExcluded';

const ProjectContainer = styled.div`
  border-style: none;
  bottom: 0;
  left: 0;
  overflow: hidden;
  font-family: Roboto, sans-serif !important;
  transition: all 0.6s ease-in-out;
  position: relative;
  background-color: #fff;
  height: auto;
  
  &.removeScroll {
    overflow: hidden;
  }

  & > p,
  & > div,
  & > a,
  & > h1,
  & > h2,
  & > h3,
  & > h4,
  & > h5,
  & > h6,
  & > select,
  & > input,
  & > label,
  & > option,
  & > ul,
  & > li,
  & > button,
  & > span,
  & > main,
  & > header,
  & > footer,
  & > b,
  & > table,
  & > tr,
  & > td,
  & > title,
  & > strong {
  /* & > * { */
    font-family: Roboto, sans-serif !important;
  }
`;

const ChildrenContainer = styled.main`
  position: relative;
  left: 50%;
  transform: translateX(-50%);
`;

const WebappBodyContainer = styled.div`
  transition: transform .6s ease-in-out, -webkit-transform .6s ease-in-out;
  
  &.menuIsOpen {
    transform: translate(290px, 0);
  }
`;

const LoadingContainer = styled.div`
  height: 4px;
  width: 100%;
  background-color: transparent;
  position: relative;
`;

const LoadingBar = styled.div`
  @keyframes gradient { 
    0%   { background-position: 0 0; }
    100% { background-position: -200% 0; }
  }
  height: 100%;
  width: 100%;
  background: transparent;
  
  &.active {
    background: repeating-linear-gradient(to right, #eee 0%, #999 50%, #eee 100%);
    background-size: 200% auto;
    background-position: 0 100%;
    animation: gradient 1s infinite;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
  }
`;

const Loading = ({ active }) => (
  <LoadingContainer>
    <LoadingBar className={active ? 'active' : ''} />
  </LoadingContainer>
);

const StickyContainer = styled.div`
  /* position: sticky; */
  top: 0;
  margin-bottom: 10px;
  /* z-index: 99999; */
`;

const Header = styled.div`
    width: 100%;
    padding: 0 10px;
    box-sizing: border-box;
`;

const YourLocation = styled.p`
  margin: 20px 0px 0px;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: capitalize;
`;

const LocationText = styled.p`
  font-size: 19px !important;
  font-weight: 500 !important;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100% - 120px);
`;

const ChangeZoneBox = styled.div`
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 7px 0;
  border-radius: 4px;
  background-color: #ffffff;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
`;

const ChangeZoneBtn = styled.button`
  display: inline-block; 
  -moz-appearance: none;
  -webkit-appearance: none;
  border: none;
  outline: none;
   
  font-size: 14px !important;
  font-stretch: normal !important;
  font-style: normal !important;
  line-height: 1.25;
  letter-spacing: normal;
  text-align: left;
  color: ${fixedColors.RC_ACTION};
  cursor: pointer;
  border: none;
  background-color: transparent;
  position: relative;

  &:before {
    width: 16px;
    height: 16px;
    content: ' ';
    background-image: url(${images.calendar.changeZone});
    position: absolute;
    left: -16px;
  }

  &:focus {
    outline: none;
  }
`;

const InnerTextOfChangeZoneBtn = styled.span`
  cursor: pointer;
  text-transform: capitalize;

  ${ChangeZoneBtn}:focus > & {
    box-shadow: 0 0 2px 2px #51a7e8;
    border-radius: 5px;
  }

  &:focus {
    outline: none;
  }
`;

const ContentHeader = styled.div`
  border: 1px solid transparent;
  border-color: ${({ primaryColor }) => (primaryColor ? `#${primaryColor}` : fixedColors.RC_ROYAL)};
  border-top: none;
  border-radius: 0 0 5px 5px;
  box-sizing: border-box;
  margin-top: -10px;
`;

// End of style

const Template = ({
  uuid,
  hasWgw,
  pluginId,
  projectId,
  isLoading,
  hasPrompt,
  districtId,
  hasCampaign,
  isModalOpen,
  hasCalendar,
  fullAddress,
  mapCityName,
  selectedLang,
  hasResources,
  setModalType,
  promptMessage,
  setIsModalOpen,
  isMultiDistrict,
  promptExpiryDate,
  isPromptMessageOpen,
  modalAlertingMessage,
  setModalConfirmAction,
  setModalAlertingMessage,
  shouldSeeDistrictSelector,
  children,
  colors,
  isActive
}) => {
  const history = useHistory();
  const location = useLocation();
  const { main_color } = colors;

  const mainContentRef = useRef();
  const promptCondition = hasPrompt
    && promptMessage.length
    && moment(promptExpiryDate).format('YYYY-MM-DD') >= (moment().format('YYYY-MM-DD'));

  /**
   * Setup Application with pluginId
   */
  useEffect(() => {
    window.addEventListener('message', campaignMessageHandler, false);
  }, [pluginId]);

  useEffect(() => {
    setModalType('alert');
    setModalConfirmAction(false);
    setIsModalOpen(false);
    setModalAlertingMessage('');
  }, []);

  const campaignMessageHandler = (event) => {
    if (typeof event.data === 'string') {
      handleAction(event.data);
    }
  };

  const campaignLink = () => `${webCampaignBaseUrl}/?projectId=${projectId}&districtId=${districtId}&uuid=${uuid}&lang=${selectedLang}&zones=&runson=webapp`;

  const decideRouting = () => {
    if (
      isMultiDistrict
      && shouldSeeDistrictSelector
    ) {
      return history.push('/select-districts');
    }
    return history.push('/zone-setup');
  };

  let exclude = false;

  // don't show NS for some districts
  if (projectId && districtId) {
    exclude = NSExcluded.some(
      (item) => item.project_id === projectId && item.district_id === districtId
    );
  }

  const date = new Date();
  const day = date.getDate();
  const month = date.getMonth();

  const displayNationalSurveyPropmt = (month === 8 || (month === 9 && day <= 31))
    || (projectId === 'master' && districtId === 'StandAloneTestDistrict');

  if (!isActive) {
    return null;
  }
  return (
    <ProjectContainer>
      <WebappBodyContainer>

        <WebappHeader />
        <ContentHeader
          primaryColor={main_color}
          fixedColors={fixedColors}

        >
          {!location.pathname.includes('campaign') && (
            <StickyContainer>
              <WebappSubHeader />
            </StickyContainer>
          )}

          {/* National survey promp */}
          {/* {
            !exclude
            && districtId
            && (location.pathname.includes('zone-setup')
              || location.pathname.includes('cal')
              || location.pathname.includes('resources')
              || location.pathname.includes('wgw')
              || location.pathname.includes('education')
            )
            && displayNationalSurveyPropmt
            && (
              <CustomMessage
                url={apis.nationalSurveyUrl(projectId, districtId, uuid, selectedLang)}
              />
            )
          } */}

          {
            (
              hasCalendar
              && promptCondition
              && isPromptMessageOpen
              && (
                location.pathname === '/cal'
                || location.pathname.includes('zone-setup')
                || location.pathname.includes('select-districts')
              )
            ) || (
                hasWgw
                && !hasCalendar
                && promptCondition
                && isPromptMessageOpen
                && (
                  location.pathname === '/wgw'
                  || location.pathname.includes('zone-setup')
                  || location.pathname.includes('select-districts')
                )
              ) || (
                !hasWgw
                && !hasCalendar
                && hasResources
                && promptCondition
                && isPromptMessageOpen
                && (
                  location.pathname === '/resources'
                  || location.pathname.includes('zone-setup')
                  || location.pathname.includes('select-districts')
                )
              )
              ? <PromptMessage />
              : null
          }

          <Loading
            active={isLoading}
          />

          {
            !hasCalendar
              && isMultiDistrict
              && location.pathname !== '/select-districts'
              && location.pathname !== '/material-list'
              && (
                location.pathname === '/wgw'
                || location.pathname === '/resources'
              )
              ? (
                <Header>
                  <YourLocation>{_('zone.your_location')}</YourLocation>
                  <ChangeZoneBox>
                    <LocationText>
                      {fullAddress || mapCityName.search_nm}
                    </LocationText>
                    <ChangeZoneBtn onClick={() => decideRouting()} tabIndex="0" aria-label="go to change the zone">
                      <InnerTextOfChangeZoneBtn tabIndex="-1">
                        {_('general.change_address')}
                      </InnerTextOfChangeZoneBtn>
                    </ChangeZoneBtn>
                  </ChangeZoneBox>
                </Header>
              ) : null
          }

          {
            hasCampaign
              ? (
                <Campaign src={campaignLink()} />
              )
              : (
                <ChildrenContainer
                  tabIndex={-1}
                  ref={mainContentRef}
                  aria-live="polite"
                >
                  {children}
                </ChildrenContainer>
              )
          }
          {
            isModalOpen
              ? (
                <AlertIt
                  text={modalAlertingMessage}
                  onCancel={() => {
                    setIsModalOpen(false);
                    setModalType('alert');
                  }}
                />
              ) : null
          }
          <WebappFooter />
        </ContentHeader>
      </WebappBodyContainer>
    </ProjectContainer>
  );
};

const mapStateToProps = (state) => ({
  uuid: state.plugin.uuid,
  zoneStr: state.zone.zoneStr,
  isLoading: state.plugin.loading,
  projectId: state.plugin.projectId,
  districtId: state.config.districtId,
  fullAddress: state.zone.fullAddress,
  mapCityName: state.zone.mapCityName,
  isModalOpen: state.alert.isModalOpen,
  selectedLang: state.config.selectedLang,
  isActive: state.config.isActive,
  isMultiDistrict: state.config.isMultiDistrict,
  modalAlertingMessage: state.alert.modalAlertingMessage,
  shouldSeeDistrictSelector: state.config.shouldSeeDistrictSelector,
  colors: state.config.templateColour ? JSON.parse(state.config.templateColour) : {},
  hasWgw: state.config.webappConfig.has_wgw,
  hasPrompt: state.config.webappConfig.has_prompt,
  isPromptMessageOpen: state.config.isPromptMessageOpen,
  hasResources: state.config.webappConfig.has_resources,
  promptMessage: state.config.webappConfig.prompt_message,
  promptExpiryDate: state.config.webappConfig.prompt_expiry_date,

  districts: Object.values(state.plugin.storeDistricts).length
    ? Object.values(state.plugin.storeDistricts)
    : [],

  hasCalendar: (
    state.config.webappConfig
    && state.config.webappConfig.has_calendar
  ) ? state.config.webappConfig.has_calendar : false,

  hasCampaign: state.campaign.isCampaignActive
    && Object.keys(state.campaign.campaignInfo)
    && Object.keys(state.campaign.campaignInfo).length,
});

const mapDispatchToProps = (dispatch) => ({
  setModalType: (modalType) => dispatch(alertSetup.setModalType(modalType)),
  setModalConfirmAction: (modalConfirmAction) => dispatch(
    alertSetup.setModalConfirmAction(modalConfirmAction)
  ),
  setModalAlertingMessage: (alert) => dispatch(alertSetup.setModalAlertingMessage(alert)),
  setIsModalOpen: (isModalOpen) => dispatch(alertSetup.setIsModalOpen(isModalOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Template);
